import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import DataService from '../../services/DataService';

const CurrentQuotes = () => {
    const [currentQuotes, setCurrentQuotes] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const fetchCurrentQuotes = async () => {
        try {
            // Récupérer tous les devis signés et non payés
            const quotes = await DataService.filter('quotes', { 
                status: 'Signed',
                paid: false 
            });

            const quotesWithDetails = await Promise.all(quotes.map(async (quote) => {
                const opportunity = await DataService.getById('opportunities', quote.opportunityId);
                if (!opportunity) return null;

                const account = await DataService.getById('accounts', opportunity.accountId);
                if (!account) return null;

                // Récupérer toutes les factures payées pour ce devis
                const paidBills = await DataService.db.bills
                    .where('quoteId')
                    .equals(quote.id)
                    .and(bill => bill.status === 'Paid')
                    .toArray();

                const quoteAmount = await quote.getTotal();
                
                // Calculer le montant total payé en additionnant toutes les factures payées
                const paidAmount = paidBills.reduce((total, bill) => {
                    return total + (typeof bill.currentBillAmount === 'number' ? bill.currentBillAmount : 0);
                }, 0);

                return {
                    accountName: account.name,
                    opportunityName: opportunity.name,
                    quoteAmount: quoteAmount,
                    paidAmount: paidAmount
                };
            }));

            setCurrentQuotes(quotesWithDetails.filter(quote => quote !== null));
        } catch (error) {
            console.error('Erreur lors de la récupération des devis en cours:', error);
        }
    };

    useEffect(() => {
        fetchCurrentQuotes();
    }, []);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedQuotes = () => {
        if (!sortConfig.key) return currentQuotes;

        return [...currentQuotes].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'account':
                    aValue = a.accountName;
                    bValue = b.accountName;
                    break;
                case 'project':
                    aValue = a.opportunityName;
                    bValue = b.opportunityName;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return faSort;
        }
        return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
    };

    return (
        <div className="panel">
            <div className="panel-heading">
                <div className="panel-header-content">
                    <span className="icon-text">
                        <span className="icon">
                            <FontAwesomeIcon icon={faFileContract} />
                        </span>
                        <span> Devis en cours</span>
                    </span>
                </div>
            </div>
            <div className="panel-block">
                <div className="table-container">
                    <table className="table is-fullwidth is-striped is-hoverable mb-0">
                        <thead>
                            <tr>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('account')}
                                    >
                                        Compte <FontAwesomeIcon icon={getSortIcon('account')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('project')}
                                    >
                                        Projet <FontAwesomeIcon icon={getSortIcon('project')} className="ml-1" />
                                    </button>
                                </th>
                                <th className="has-text-right">Montant devis</th>
                                <th className="has-text-right">Montant payé</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedQuotes().map((quote, index) => (
                                <tr key={index}>
                                    <td>{quote.accountName}</td>
                                    <td>{quote.opportunityName}</td>
                                    <td className="has-text-right">
                                        {quote.quoteAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                    </td>
                                    <td className="has-text-right">
                                        {quote.paidAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                    </td>
                                </tr>
                            ))}
                            {currentQuotes.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="has-text-centered">Aucun devis en cours</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="panel-footer"></div>
        </div>
    );
};

export default CurrentQuotes;
